import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";

import { faEnvelope, faLink } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
	faGithub,
	faLinkedin,
	faMedium,
	faNpm,
} from "@fortawesome/free-brands-svg-icons";

import Logo from "../components/common/logo";
import Footer from "../components/common/footer";
import NavBar from "../components/common/navBar";
import Seo from "../components/common/seo";
import AllProjects from "../components/projects/allProjects";

import INFO from "../data/user";
import { personSchema, projectListSchema, serviceSchema, websiteSchema } from "../data/schema";

import "./styles/homepage.css";

const Homepage = () => {
	const [stayLogo, setStayLogo] = useState(false);
	const [logoSize, setLogoSize] = useState(80);
	const [oldLogoSize, setOldLogoSize] = useState(80);

	useEffect(() => {
		window.scrollTo(0, 0);
	}, []);

	useEffect(() => {
		const handleScroll = () => {
			let scroll = Math.round(window.pageYOffset, 2);

			let newLogoSize = 80 - (scroll * 4) / 10;

			if (newLogoSize < oldLogoSize) {
				if (newLogoSize > 40) {
					setLogoSize(newLogoSize);
					setOldLogoSize(newLogoSize);
					setStayLogo(false);
				} else {
					setStayLogo(true);
				}
			} else {
				setLogoSize(newLogoSize);
				setStayLogo(false);
			}
		};

		window.addEventListener("scroll", handleScroll);
		return () => window.removeEventListener("scroll", handleScroll);
	}, [logoSize, oldLogoSize]);

	const logoStyle = {
		display: "flex",
		position: stayLogo ? "fixed" : "relative",
		top: stayLogo ? "3vh" : "auto",
		zIndex: 999,
		border: stayLogo ? "1px solid white" : "none",
		borderRadius: stayLogo ? "50%" : "none",
		boxShadow: stayLogo ? "0px 4px 10px rgba(0, 0, 0, 0.25)" : "none",
	};

	const contactIcon = {
		Email: faEnvelope,
		LinkedIn: faLinkedin,
		GitHub: faGithub,
		NPM: faNpm,
		Medium: faMedium,
		Linktree: faLink,
	};

	return (
		<React.Fragment>
			<Seo
				page="home"
				schema={[personSchema, websiteSchema, serviceSchema, projectListSchema]}
			/>

			<div className="page-content">
				<NavBar active="home" />
				<div className="content-wrapper">
					<div className="homepage-logo-container">
						<div style={logoStyle}>
							<Logo width={logoSize} link={false} />
						</div>
					</div>

					<main className="homepage-container">
						<div className="homepage-first-area">
							<div className="homepage-first-area-left-side">
								<h1 className="title homepage-title">
									{INFO.homepage.title}
								</h1>

								<div className="subtitle homepage-subtitle">
									{INFO.homepage.description}
								</div>

								<div className="homepage-ctas">
									<Link
										to={INFO.homepage.primaryCTA.href}
										className="homepage-cta homepage-cta-primary"
									>
										{INFO.homepage.primaryCTA.label}
									</Link>
									<a
										href={INFO.homepage.secondaryCTA.href}
										className="homepage-cta homepage-cta-secondary"
									>
										{INFO.homepage.secondaryCTA.label}
									</a>
								</div>
							</div>

							<div className="homepage-first-area-right-side">
								<div className="homepage-image-container">
									<div className="homepage-image-wrapper">
										<img
											src="homepage.jpg"
											alt="Sharjeel Faiq, full stack developer in Islamabad, Pakistan"
											className="homepage-image"
											width="370"
											height="370"
											decoding="async"
											fetchpriority="high"
										/>
									</div>
								</div>
							</div>
						</div>

						<div className="homepage-socials">
							<a
								href={INFO.socials.github}
								target="_blank"
								rel="noopener noreferrer"
								aria-label="GitHub"
							>
								<FontAwesomeIcon
									icon={faGithub}
									className="homepage-social-icon"
								/>
							</a>
							<a
								href={INFO.socials.linkedin}
								target="_blank"
								rel="noopener noreferrer"
								aria-label="LinkedIn"
							>
								<FontAwesomeIcon
									icon={faLinkedin}
									className="homepage-social-icon"
								/>
							</a>
							<a
								href={INFO.main.emailHref}
								aria-label={`Email ${INFO.main.name}`}
							>
								<FontAwesomeIcon
									icon={faEnvelope}
									className="homepage-social-icon"
								/>
							</a>
						</div>

						<section className="homepage-section homepage-trust" aria-label="Trust metrics">
							{INFO.homepage.trustPoints.map((point) => (
								<div className="homepage-trust-item" key={point}>
									{point}
								</div>
							))}
						</section>

						<section className="homepage-section homepage-about" id="about">
							<div>
								<div className="homepage-section-kicker">About</div>
								<h2 className="homepage-section-title">{INFO.about.title}</h2>
							</div>
							<div>
								<p className="homepage-section-copy">{INFO.about.description}</p>
								<div className="homepage-highlight-list">
									{INFO.about.highlights.map((highlight) => (
										<div className="homepage-highlight" key={highlight}>
											{highlight}
										</div>
									))}
								</div>
							</div>
						</section>

						<section className="homepage-section" id="services">
							<div className="homepage-section-kicker">Services</div>
							<h2 className="homepage-section-title">How I can help</h2>
							<div className="homepage-card-grid homepage-service-grid">
								{INFO.services.map((service) => (
									<div className="homepage-card" key={service.title}>
										<h3>{service.title}</h3>
										<p>{service.description}</p>
									</div>
								))}
							</div>
						</section>

						<section className="homepage-section" id="skills">
							<div className="homepage-section-kicker">Skills</div>
							<h2 className="homepage-section-title">Stack coverage</h2>
							<div className="homepage-card-grid homepage-skill-grid">
								{INFO.skills.map((skillGroup) => (
									<div className="homepage-card" key={skillGroup.group}>
										<h3>{skillGroup.group}</h3>
										<div className="homepage-tag-list">
											{skillGroup.items.map((item) => (
												<span className="homepage-tag" key={item}>
													{item}
												</span>
											))}
										</div>
									</div>
								))}
							</div>
						</section>

						<section className="homepage-section" id="experience">
							<div className="homepage-section-kicker">Experience</div>
							<h2 className="homepage-section-title">Recent work history</h2>
							<div className="homepage-timeline">
								{INFO.experience.map((item) => (
									<div className="homepage-timeline-item" key={`${item.company}-${item.role}`}>
										<div className="homepage-timeline-marker" />
										<div className="homepage-timeline-content">
											<div className="homepage-timeline-meta">
												<span>{item.period}</span>
												<span>{item.location}</span>
											</div>
											<h3>{item.role}</h3>
											<div className="homepage-timeline-company">{item.company}</div>
											<p>{item.description}</p>
										</div>
									</div>
								))}
							</div>
						</section>

						<section className="homepage-section homepage-projects" id="projects">
							<div className="homepage-section-kicker">Featured Projects</div>
							<h2 className="homepage-section-title">
								Selected full-stack work
							</h2>
							<AllProjects />
						</section>

						<section className="homepage-section" id="open-source">
							<div className="homepage-section-kicker">Open Source</div>
							<h2 className="homepage-section-title">Reusable engineering work</h2>
							<div className="homepage-card-grid homepage-open-source-grid">
								{INFO.openSource.map((item) => (
									<a
										className="homepage-card homepage-open-source-card"
										key={item.title}
										href={item.link}
										target="_blank"
										rel="noopener noreferrer"
									>
										<h3>{item.title}</h3>
										<p>{item.description}</p>
										<div className="homepage-tag-list">
											{item.tags.map((tag) => (
												<span className="homepage-tag" key={tag}>
													{tag}
												</span>
											))}
										</div>
									</a>
								))}
							</div>
						</section>

						<section className="homepage-section homepage-testimonial" id="testimonial">
							<div>
								<div className="homepage-section-kicker">Testimonial</div>
								<h2 className="homepage-section-title">
									{INFO.testimonial.title}
								</h2>
							</div>
							<div className="homepage-testimonial-panel">
								<p>{INFO.testimonial.description}</p>
								{INFO.testimonial.video && (
									<a
										href={INFO.testimonial.video}
										target="_blank"
										rel="noopener noreferrer"
										className="homepage-cta homepage-cta-secondary"
									>
										Watch testimonial
									</a>
								)}
								<a
									href={INFO.testimonial.link}
									target="_blank"
									rel="noopener noreferrer"
									className="homepage-text-link"
								>
									{INFO.testimonial.linkLabel}
								</a>
							</div>
						</section>

						<section className="homepage-section homepage-contact" id="contact">
							<div>
								<div className="homepage-section-kicker">Contact</div>
								<h2 className="homepage-section-title">
									{INFO.contact.title}
								</h2>
								<p className="homepage-section-copy">
									{INFO.contact.description}
								</p>
							</div>
							<div className="homepage-contact-list">
								{INFO.contact.links.map((link) => (
									<a
										className="homepage-contact-link"
										href={link.href}
										key={link.label}
										target={link.href.startsWith("http") ? "_blank" : undefined}
										rel={link.href.startsWith("http") ? "noopener noreferrer" : undefined}
									>
										<FontAwesomeIcon icon={contactIcon[link.label] || faLink} />
										<span>
											<strong>{link.label}</strong>
											{link.value}
										</span>
									</a>
								))}
							</div>
						</section>

						<div className="page-footer">
							<Footer />
						</div>
					</main>
				</div>
			</div>
		</React.Fragment>
	);
};

export default Homepage;
